import React, { useCallback, useEffect, useState, useRef, Component } from 'react';
import { ShieldCheckIcon, PencilIcon, TrashIcon, PlusIcon, MicIcon, SquareIcon, XIcon, LoaderIcon, AlertCircleIcon, RefreshCwIcon, CheckCircle2Icon, SparklesIcon, CheckIcon, AlertTriangleIcon, FileWarningIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ScreenProps, PermitItem } from '../../types/ptp';
import { PrimaryButton } from '../ui/PrimaryButton';
import { EditItemModal } from '../ui/EditItemModal';
import { getSavedApiKey, chatCompletionWithRetry, transcribeWithRetry } from '../../config/openai';
import { PERMITS_GENERATION_PROMPT, PERMITS_VOICE_CORRECTION_PROMPT } from '../../config/aiPrompts';
import { useI18n } from '../../config/i18n';
// ── Constants ────────────────────────────────────────────────────────────────
const STANDARD_PERMITS: {
  name: string;
  name_es: string;
}[] = [{
  name: 'Hot Work',
  name_es: 'Trabajo en Caliente'
}, {
  name: 'Energized Systems/LOTO',
  name_es: 'Sistemas Energizados/LOTO'
}, {
  name: 'Ladders Last Permit',
  name_es: 'Permiso de Escaleras como Último Recurso'
}, {
  name: 'Ground Penetration',
  name_es: 'Penetración del Suelo'
}, {
  name: 'Confined Space',
  name_es: 'Espacio Confinado'
}, {
  name: 'Roof Access',
  name_es: 'Acceso al Techo'
}];
let permitIdCounter = 0;
function createPermitItem(name: string, needed: boolean, name_es?: string, exists: boolean | null = null): PermitItem {
  return {
    id: `permit-${Date.now()}-${++permitIdCounter}`,
    name,
    name_es,
    needed,
    exists
  };
}
// ── Main Component ───────────────────────────────────────────────────────────
export function PermitsScreen({
  data,
  updateData,
  onNext
}: ScreenProps) {
  const {
    lang,
    t
  } = useI18n();
  // ── State ──────────────────────────────────────────────────────────────────
  const [items, setItems] = useState<PermitItem[]>(() => {
    if (data.permits && data.permits.length > 0) return [...data.permits];
    return [];
  });
  // AI Generation State
  const [isAnalyzing, setIsAnalyzing] = useState(() => {
    return (!data.permits || data.permits.length === 0) && !!data.taskDescription;
  });
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);
  const hasRunAI = useRef(false);
  // Edit Modal State
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<{
    id: string;
    text: string;
  } | null>(null);
  // Voice Modal State
  const [showVoiceModal, setShowVoiceModal] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [hasRecorded, setHasRecorded] = useState(false);
  const [voiceTranscription, setVoiceTranscription] = useState('');
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [extractionApplied, setExtractionApplied] = useState(false);
  const [extractionSummary, setExtractionSummary] = useState('');
  // MediaRecorder refs
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  // ── Sync to Parent ─────────────────────────────────────────────────────────
  const syncToParent = useCallback((newItems: PermitItem[]) => {
    updateData({
      permits: newItems
    });
  }, [updateData]);
  // ── Initial AI Generation ──────────────────────────────────────────────────
  useEffect(() => {
    if (hasRunAI.current) return;
    hasRunAI.current = true;
    if (items.length > 0) {
      setIsAnalyzing(false);
      return;
    }
    const apiKey = getSavedApiKey();
    if (!apiKey || !data.taskDescription) {
      setIsAnalyzing(false);
      // Fallback: all 6 standard permits, all set to needed: false
      const fallbackItems = STANDARD_PERMITS.map(p => createPermitItem(p.name, false, p.name_es));
      setItems(fallbackItems);
      syncToParent(fallbackItems);
      return;
    }
    async function generatePermits() {
      setIsAnalyzing(true);
      setAnalyzeError(null);
      try {
        const stepsText = data.jobSteps?.length ? `\n\nMOP Steps:\n${data.jobSteps.map((s: string, i: number) => `${i + 1}. ${s}`).join('\n')}` : '';
        const equipList = data.taskEquipment?.length ? data.taskEquipment : data.equipment || [];
        const equipmentText = equipList.length ? `\n\nEquipment:\n${equipList.map((e: string) => `- ${e}`).join('\n')}` : '';
        const completion = await chatCompletionWithRetry({
          model: 'gpt-4o-mini',
          messages: [{
            role: 'system',
            content: PERMITS_GENERATION_PROMPT
          }, {
            role: 'user',
            content: `Task Description: ${data.taskDescription}${stepsText}${equipmentText}`
          }],
          temperature: 0.3,
          max_tokens: 500
        });
        const raw = completion.choices[0]?.message?.content?.trim() || '';
        let jsonStr = raw;
        const fenceMatch = raw.match(/```(?:json)?\s*([\s\S]*?)```/);
        if (fenceMatch) jsonStr = fenceMatch[1].trim();
        const parsed = JSON.parse(jsonStr) as {
          permits: {
            name: string;
            name_es?: string;
            needed: boolean;
          }[];
        };
        if (!Array.isArray(parsed.permits) || parsed.permits.length === 0) {
          throw new Error('Invalid response structure');
        }
        const newItems = parsed.permits.map(p => createPermitItem(p.name, p.needed, p.name_es));
        setItems(newItems);
        syncToParent(newItems);
      } catch (err: any) {
        console.error('[AI Permits Generation] Failed:', err);
        if (err?.status === 401) {
          setAnalyzeError('Invalid OpenAI API key. You can manage permits manually.');
        } else {
          setAnalyzeError(`AI generation failed: ${err?.message || String(err)}`);
        }
        // Fallback
        const fallbackItems = STANDARD_PERMITS.map(p => createPermitItem(p.name, false, p.name_es));
        setItems(fallbackItems);
        syncToParent(fallbackItems);
      } finally {
        setIsAnalyzing(false);
      }
    }
    generatePermits();
  }, [data.taskDescription, data.jobSteps, data.taskEquipment, items.length, syncToParent]);
  // Cleanup streams
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
    };
  }, []);
  // ── Item Handlers ──────────────────────────────────────────────────────────
  const handleRemove = (id: string) => {
    const newItems = items.filter(i => i.id !== id);
    setItems(newItems);
    syncToParent(newItems);
  };
  const handleToggleNeeded = (id: string) => {
    const newItems = items.map(item => {
      if (item.id === id) {
        const newNeeded = !item.needed;
        return {
          ...item,
          needed: newNeeded,
          // Reset exists when toggling needed off
          exists: newNeeded ? item.exists : null
        };
      }
      return item;
    });
    setItems(newItems);
    syncToParent(newItems);
  };
  const handleSetExists = (id: string, value: boolean) => {
    const newItems = items.map(item => {
      if (item.id === id) {
        return {
          ...item,
          exists: item.exists === value ? null : value
        };
      }
      return item;
    });
    setItems(newItems);
    syncToParent(newItems);
  };
  const handleEditStart = (item: PermitItem) => {
    setEditingItem({
      id: item.id,
      text: lang === 'es' && item.name_es ? item.name_es : item.name
    });
    setIsEditModalOpen(true);
  };
  const handleEditSave = (newText: string) => {
    if (!editingItem) return;
    const newItems = items.map(item => {
      if (item.id === editingItem.id) {
        if (lang === 'es') {
          return {
            ...item,
            name_es: newText
          };
        }
        return {
          ...item,
          name: newText
        };
      }
      return item;
    });
    setItems(newItems);
    syncToParent(newItems);
    setIsEditModalOpen(false);
    setEditingItem(null);
  };
  const handleAdd = () => {
    const newItem = createPermitItem('New permit', false);
    const newItems = [...items, newItem];
    setItems(newItems);
    syncToParent(newItems);
    handleEditStart(newItem);
  };
  const handleRetryAnalysis = () => {
    hasRunAI.current = false;
    setItems([]);
  };
  // ── Computed ───────────────────────────────────────────────────────────────
  const neededPermits = items.filter(i => i.needed);
  const missingPermits = neededPermits.filter(i => i.exists === false);
  const unsetPermits = neededPermits.filter(i => i.exists === null);
  const allNeededPermitsSet = neededPermits.every(i => i.exists !== null);
  // ── Voice Recording ────────────────────────────────────────────────────────
  const startRecording = useCallback(async () => {
    setVoiceError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true
      });
      streamRef.current = stream;
      chunksRef.current = [];
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm'
      });
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.ondataavailable = e => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mediaRecorder.start(250);
      setIsRecording(true);
      setVoiceTranscription('');
      setHasRecorded(false);
      setExtractionApplied(false);
      setExtractionSummary('');
    } catch (err) {
      console.error('Microphone access denied:', err);
      setVoiceError('Microphone access denied. Please allow permission and try again.');
    }
  }, []);
  const stopRecording = useCallback(async () => {
    const mediaRecorder = mediaRecorderRef.current;
    if (!mediaRecorder || mediaRecorder.state === 'inactive') {
      setIsRecording(false);
      setVoiceError('Recording failed. Please try again.');
      return;
    }
    setIsRecording(false);
    setIsTranscribing(true);
    setVoiceError(null);
    const audioBlob = await new Promise<Blob>(resolve => {
      mediaRecorder.onstop = () => {
        resolve(new Blob(chunksRef.current, {
          type: 'audio/webm'
        }));
      };
      mediaRecorder.stop();
    });
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (audioBlob.size < 1000) {
      setIsTranscribing(false);
      setVoiceError('Recording too short. Speak for at least 2 seconds.');
      return;
    }
    try {
      const file = new File([audioBlob], 'permits.webm', {
        type: 'audio/webm'
      });
      const response = await transcribeWithRetry(file, {
        model: 'whisper-1',
        language: lang === 'es' ? 'es' : 'en'
      });
      const rawText = response.text.trim();
      if (!rawText) {
        setIsTranscribing(false);
        setVoiceError('No speech detected. Please speak clearly and try again.');
        return;
      }
      setVoiceTranscription(rawText);
      setHasRecorded(true);
      setIsTranscribing(false);
    } catch (err: any) {
      console.error('[Whisper] Transcription failed:', err);
      setIsTranscribing(false);
      const statusCode = err?.status || err?.statusCode;
      if (statusCode === 401) {
        setVoiceError('Invalid OpenAI API key.');
      } else if (statusCode === 429) {
        setVoiceError('Rate limit reached. Please wait and try again.');
      } else {
        setVoiceError(`Transcription failed: ${err?.message || String(err)}`);
      }
    }
  }, [lang]);
  const handleRecordToggle = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };
  // ── Apply Voice Extraction ─────────────────────────────────────────────────
  const handleApplyExtraction = async () => {
    if (!voiceTranscription) return;
    setIsExtracting(true);
    setVoiceError(null);
    try {
      const completion = await chatCompletionWithRetry({
        model: 'gpt-4o-mini',
        messages: [{
          role: 'system',
          content: PERMITS_VOICE_CORRECTION_PROMPT
        }, {
          role: 'user',
          content: userMessage
        }],
        temperature: 0.3,
        max_tokens: 500
      });
      const raw = completion.choices[0]?.message?.content?.trim() || '';
      let jsonStr = raw;
      const fenceMatch = raw.match(/```(?:json)?\s*([\s\S]*?)```/);
      if (fenceMatch) jsonStr = fenceMatch[1].trim();
      const parsed = JSON.parse(jsonStr) as {
        permits: {
          name: string;
          name_es?: string;
          needed: boolean;
        }[];
      };
      if (!Array.isArray(parsed.permits) || parsed.permits.length === 0) {
        throw new Error('No permits detected in response');
      }
      // Merge: preserve exists status for permits that still exist
      const updatedItems = parsed.permits.map(p => {
        const existing = items.find(i => i.name.toLowerCase() === p.name.toLowerCase());
        return createPermitItem(p.name, p.needed, p.name_es, existing && p.needed ? existing.exists : null);
      });
      setItems(updatedItems);
      syncToParent(updatedItems);
      const diff = updatedItems.length - items.length;
      const summary = diff > 0 ? `Updated list — ${diff} permit${diff !== 1 ? 's' : ''} added` : diff < 0 ? `Updated list — ${Math.abs(diff)} permit${Math.abs(diff) !== 1 ? 's' : ''} removed` : `Updated list — ${updatedItems.length} permits`;
      setExtractionApplied(true);
      setExtractionSummary(summary);
      setTimeout(() => {
        setShowVoiceModal(false);
        setTimeout(() => {
          setHasRecorded(false);
          setVoiceTranscription('');
          setExtractionApplied(false);
          setExtractionSummary('');
        }, 300);
      }, 1200);
    } catch (err: any) {
      console.error('[GPT Extraction] Failed:', err);
      setVoiceError(`Failed to process permits: ${err?.message || String(err)}`);
    } finally {
      setIsExtracting(false);
    }
  };
  // ── Render Loading ─────────────────────────────────────────────────────────
  if (isAnalyzing) {
    return <div className="flex flex-col h-full items-center justify-center" data-id="element-574">
        <motion.div animate={{
        rotate: 360
      }} transition={{
        repeat: Infinity,
        duration: 1.5,
        ease: 'linear'
      }} className="mb-5" data-id="element-575">
          <LoaderIcon className="w-10 h-10 text-yellow-500" data-id="element-576" />
        </motion.div>
        <p className="text-gray-900 font-semibold text-base mb-1" data-id="element-577">
          {t('permits.analyzing')}
        </p>
        <p className="text-gray-400 text-xs text-center max-w-[220px]" data-id="element-578">
          {t('permits.analyzingSub')}
        </p>
      </div>;
  }
  // ── Main Render ────────────────────────────────────────────────────────────
  return <div className="flex flex-col h-full relative" data-id="element-579">
      <div className="mb-3" data-id="element-580">
        <div className="flex items-center justify-between mb-2" data-id="element-581">
          <h2 className="text-lg font-bold text-gray-900" data-id="element-582">
            {t('permits.title')}
          </h2>
          <button onClick={() => {
          setShowVoiceModal(true);
          setVoiceError(null);
          setExtractionApplied(false);
          setExtractionSummary('');
        }} className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-full px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors" data-id="element-583">
            <MicIcon className="w-3.5 h-3.5 text-yellow-500" data-id="element-584" />
            {t('permits.voiceCorrect')}
          </button>
        </div>
        <p className="text-gray-500 text-sm mb-4" data-id="element-585">{t('permits.subtitle')}</p>

        <div className="bg-white border border-gray-200 rounded-xl p-3 border-l-4 border-l-yellow-500" data-id="element-586">
          <div className="flex items-center gap-2" data-id="element-587">
            <SparklesIcon className="w-4 h-4 text-yellow-500 flex-shrink-0" data-id="element-588" />
            <h3 className="text-gray-900 font-bold text-base leading-snug" data-id="element-589">
              {lang === 'es' && data.taskTitle_es ? data.taskTitle_es : data.taskTitle || 'Task Permits'}
            </h3>
          </div>
        </div>
      </div>

      <AnimatePresence data-id="element-590">
        {analyzeError && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="overflow-hidden mb-3" data-id="element-591">
            <div className="bg-amber-50 border border-amber-300 rounded-lg px-3 py-2.5 flex items-start gap-2.5" data-id="element-592">
              <AlertCircleIcon className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-593" />
              <div className="flex-1 min-w-0" data-id="element-594">
                <p className="text-amber-700 text-xs leading-relaxed" data-id="element-595">
                  {analyzeError}
                </p>
              </div>
              <button onClick={handleRetryAnalysis} className="flex items-center gap-1 px-2 py-1 rounded bg-amber-100 text-amber-600 text-[10px] font-bold uppercase tracking-wider flex-shrink-0 hover:bg-amber-200 transition-colors" data-id="element-596">
                <RefreshCwIcon className="w-3 h-3" data-id="element-597" />
                Retry
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Missing permits warning */}
      <AnimatePresence data-id="element-598">
        {missingPermits.length > 0 && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="overflow-hidden mb-3" data-id="element-599">
            <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 flex items-start gap-2.5" data-id="element-600">
              <AlertTriangleIcon className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" data-id="element-601" />
              <div className="flex-1 min-w-0" data-id="element-602">
                <p className="text-red-600 text-xs font-bold mb-0.5" data-id="element-603">
                  {t('permits.missingWarning')}
                </p>
                <p className="text-red-500 text-xs leading-relaxed" data-id="element-604">
                  {missingPermits.map(p => lang === 'es' && p.name_es ? p.name_es : p.name).join(', ')}
                </p>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      <div className="flex-1 overflow-y-auto pb-20 -mx-4 px-4" data-id="element-605">
        {items.length === 0 ? <div className="bg-gray-50 border border-gray-200 border-dashed rounded-xl p-8 flex flex-col items-center justify-center text-center mt-4" data-id="element-606">
            <p className="text-gray-400 text-sm font-medium mb-1" data-id="element-607">
              {t('permits.noPermits')}
            </p>
            <p className="text-gray-400 text-xs" data-id="element-608">
              Add permits manually or use voice to describe them
            </p>
          </div> : <div className="space-y-3 mt-2" data-id="element-609">
            <AnimatePresence data-id="element-610">
              {items.map((item, index) => {
            const displayName = lang === 'es' && item.name_es ? item.name_es : item.name;
            return <motion.div key={item.id} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              scale: 0.95
            }} transition={{
              delay: index * 0.05
            }} className={`bg-white border rounded-xl overflow-hidden transition-colors ${item.needed && item.exists === false ? 'border-red-300 ring-1 ring-red-100' : item.needed && item.exists === true ? 'border-green-300 ring-1 ring-green-100' : 'border-gray-200'}`} data-id="element-611">
                    <div className="p-3" data-id="element-612">
                      <div className="flex items-center gap-3" data-id="element-613">
                        {/* Needed toggle */}
                        <button onClick={() => handleToggleNeeded(item.id)} className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-all ${item.needed ? 'bg-yellow-400 text-gray-900 shadow-sm shadow-yellow-400/30' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`} data-id="element-614">
                          <CheckIcon className="w-3.5 h-3.5" data-id="element-615" />
                        </button>

                        <p className={`flex-1 text-sm font-semibold leading-snug min-w-0 ${item.needed ? 'text-gray-900' : 'text-gray-400'}`} data-id="element-616">
                          {displayName}
                        </p>

                        <div className="flex items-center gap-0.5 flex-shrink-0" data-id="element-617">
                          <button onClick={() => handleEditStart(item)} className="p-1.5 text-gray-300 hover:text-yellow-500 transition-colors" data-id="element-618">
                            <PencilIcon className="w-3.5 h-3.5" data-id="element-619" />
                          </button>
                          <button onClick={() => handleRemove(item.id)} className="p-1.5 text-gray-300 hover:text-red-500 transition-colors" data-id="element-620">
                            <TrashIcon className="w-3.5 h-3.5" data-id="element-621" />
                          </button>
                        </div>
                      </div>

                      {/* Exists status — only shown when needed */}
                      {item.needed && <div className="flex items-center gap-2 mt-2.5 ml-10" data-id="element-622">
                          <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider" data-id="element-623">
                            {t('permits.permitStatus')}
                          </span>
                          <div className="flex items-center gap-1.5" data-id="element-624">
                            <button onClick={() => handleSetExists(item.id, true)} className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all border ${item.exists === true ? 'bg-green-50 border-green-300 text-green-600' : 'bg-white border-gray-200 text-gray-400 hover:border-green-200 hover:text-green-500'}`} data-id="element-625">
                              <CheckCircle2Icon className="w-3 h-3" data-id="element-626" />
                              {t('permits.obtained')}
                            </button>
                            <button onClick={() => handleSetExists(item.id, false)} className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all border ${item.exists === false ? 'bg-red-50 border-red-300 text-red-500' : 'bg-white border-gray-200 text-gray-400 hover:border-red-200 hover:text-red-500'}`} data-id="element-627">
                              <FileWarningIcon className="w-3 h-3" data-id="element-628" />
                              {t('permits.missing')}
                            </button>
                          </div>
                        </div>}
                    </div>
                  </motion.div>;
          })}
            </AnimatePresence>
          </div>}

        <motion.button whileTap={{
        scale: 0.98
      }} onClick={handleAdd} className="mt-4 w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 font-medium text-sm flex items-center justify-center gap-2 hover:border-gray-400 hover:text-gray-700 transition-colors" data-id="element-629">
          <PlusIcon className="w-4 h-4" data-id="element-630" />
          {t('permits.addPermit')}
        </motion.button>
      </div>

      <div className="sticky bottom-0 z-10 pt-3 pb-2 bg-gray-50" data-id="element-631">
        <PrimaryButton onClick={onNext} disabled={items.length === 0} data-id="element-632">
          {t('continue')}
        </PrimaryButton>
      </div>

      {/* ── Edit Modal ──────────────────────────────────────────────────────── */}
      <EditItemModal isOpen={isEditModalOpen} title={t('permits.editPermit')} initialText={editingItem?.text || ''} onSave={handleEditSave} onCancel={() => {
      setIsEditModalOpen(false);
      setEditingItem(null);
    }} data-id="element-633" />

      {/* ── Voice Correction Modal ──────────────────────────────────────────── */}
      <AnimatePresence data-id="element-634">
        {showVoiceModal && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => !isRecording && !isTranscribing && !isExtracting && setShowVoiceModal(false)} className="absolute inset-0 bg-black/30 backdrop-blur-sm z-40" data-id="element-635" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-200 rounded-t-2xl z-50 px-5 pt-4 pb-6 max-h-[75%] flex flex-col overflow-hidden" data-id="element-636">
              <div className="flex items-center justify-between mb-2" data-id="element-637">
                <h3 className="text-lg font-bold text-gray-900" data-id="element-638">
                  {t('permits.voiceCorrect')}
                </h3>
                <button onClick={() => setShowVoiceModal(false)} disabled={isRecording || isTranscribing || isExtracting} className="p-2 text-gray-400 hover:text-gray-900 transition-colors rounded-full hover:bg-gray-100 disabled:opacity-50" data-id="element-639">
                  <XIcon className="w-5 h-5" data-id="element-640" />
                </button>
              </div>

              <p className="text-gray-500 text-sm mb-4" data-id="element-641">
                {t('permits.voiceDesc')}
              </p>

              {extractionApplied ? <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="flex-1 flex flex-col items-center justify-center space-y-3" data-id="element-642">
                  <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              type: 'spring',
              stiffness: 400,
              damping: 15
            }} data-id="element-643">
                    <CheckCircle2Icon className="w-12 h-12 text-green-600" data-id="element-644" />
                  </motion.div>
                  <p className="text-gray-900 font-semibold text-sm" data-id="element-645">
                    {t('permits.extracted')}
                  </p>
                  <p className="text-gray-500 text-xs" data-id="element-646">{extractionSummary}</p>
                </motion.div> : <>
                  <div className="flex-1 flex flex-col items-center justify-center space-y-4 mb-4 min-h-0" data-id="element-647">
                    {isExtracting ? <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} className="flex flex-col items-center space-y-3" data-id="element-648">
                        <motion.div animate={{
                  rotate: 360
                }} transition={{
                  repeat: Infinity,
                  duration: 1.2,
                  ease: 'linear'
                }} data-id="element-649">
                          <LoaderIcon className="w-10 h-10 text-yellow-500" data-id="element-650" />
                        </motion.div>
                        <p className="text-gray-900 font-medium text-sm" data-id="element-651">
                          {t('permits.extracting')}
                        </p>
                      </motion.div> : isTranscribing ? <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} className="flex flex-col items-center space-y-3" data-id="element-652">
                        <motion.div animate={{
                  rotate: 360
                }} transition={{
                  repeat: Infinity,
                  duration: 1,
                  ease: 'linear'
                }} data-id="element-653">
                          <LoaderIcon className="w-10 h-10 text-blue-600" data-id="element-654" />
                        </motion.div>
                        <p className="text-gray-900 font-medium text-sm" data-id="element-655">
                          Transcribing…
                        </p>
                      </motion.div> : <>
                        <div className="relative flex items-center justify-center" data-id="element-656">
                          {isRecording && <motion.div animate={{
                    scale: [1, 1.3, 1]
                  }} transition={{
                    repeat: Infinity,
                    duration: 1.5
                  }} className="absolute w-24 h-24 bg-yellow-100 rounded-full" data-id="element-657" />}
                          {isRecording && <motion.div animate={{
                    scale: [1, 1.15, 1]
                  }} transition={{
                    repeat: Infinity,
                    duration: 1.5,
                    delay: 0.2
                  }} className="absolute w-20 h-20 bg-yellow-200 rounded-full" data-id="element-658" />}
                          <motion.button whileTap={{
                    scale: 0.9
                  }} onClick={handleRecordToggle} className={`relative z-10 w-16 h-16 rounded-full flex items-center justify-center shadow-xl transition-colors ${isRecording ? 'bg-red-500 shadow-red-400/30' : 'bg-yellow-400 shadow-yellow-400/30'}`} data-id="element-659">
                            {isRecording ? <SquareIcon className="w-6 h-6 text-white fill-current" data-id="element-660" /> : <MicIcon className="w-6 h-6 text-gray-900" data-id="element-661" />}
                          </motion.button>
                        </div>

                        <div className="text-center h-6" data-id="element-662">
                          <p className={`font-medium text-sm ${isRecording ? 'text-red-500 animate-pulse' : 'text-gray-500'}`} data-id="element-663">
                            {isRecording ? 'Recording…' : hasRecorded ? 'Tap to re-record' : 'Tap to start'}
                          </p>
                        </div>
                      </>}

                    {voiceError && <p className="text-red-500 text-xs text-center max-w-[250px]" data-id="element-664">
                        {voiceError}
                      </p>}

                    {hasRecorded && voiceTranscription && !isExtracting && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} transition={{
                duration: 0.3
              }} className="w-full p-3 rounded-xl bg-gray-50 border border-gray-200 overflow-y-auto max-h-[20vh]" data-id="element-665">
                        <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-1" data-id="element-666">
                          Transcription
                        </p>
                        <p className="text-gray-700 text-sm leading-snug" data-id="element-667">
                          "{voiceTranscription}"
                        </p>
                      </motion.div>}
                  </div>

                  {!isTranscribing && !isExtracting && <PrimaryButton onClick={handleApplyExtraction} disabled={!hasRecorded || !voiceTranscription} data-id="element-668">
                      Apply to Permits List
                    </PrimaryButton>}
                </>}
            </motion.div>
          </>}
      </AnimatePresence>
    </div>;
}